/**
 * Events Service
 * Handles all calendar event-related data operations with workspace-first RLS
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database, Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { logger } from '@/lib/logger';
import { getSupabaseErrorMessage } from '@/lib/errorHandler';
import { WorkspaceScope, applyWorkspaceScope } from '@/lib/workspaceScope';

export type Event = Tables<'events'>;

export interface CreateEventInput extends Omit<TablesInsert<'events'>, 'workspace_id'> {
  workspace_id: string;
}

export interface UpdateEventInput extends Partial<TablesUpdate<'events'>> {
  id: string;
  workspace_id: string;
}

export class EventsService {
  /**
   * Get all events for a workspace scope in a date range
   */
  static async getEvents(
    scope: WorkspaceScope,
    startDate: string,
    endDate: string
  ): Promise<Event[]> {
    // Try to fetch with all fields first
    let query = supabase
      .from('events')
      .select('*')
      .gte('start_at', startDate)
      .lte('start_at', endDate);
    
    // Apply workspace scope filter
    query = applyWorkspaceScope(query, scope);
    
    query = query.order('start_at', { ascending: true });

    const { data, error } = await query;

    // If error is about missing columns, try with core fields only
    if (error && (error.code === 'PGRST204' || error.message?.includes('schema cache'))) {
      logger.warn('[EventsService.getEvents] Schema cache issue, retrying with core fields only', {
        errorCode: error.code,
        errorMessage: error.message,
        scope
      });
      
      // Retry with only core fields
      let coreQuery = supabase
        .from('events')
        .select('id, workspace_id, lead_id, type, title, start_at, end_at, notes, created_at')
        .gte('start_at', startDate)
        .lte('start_at', endDate);
      
      // Apply workspace scope filter
      coreQuery = applyWorkspaceScope(coreQuery, scope);
      
      const { data: coreData, error: coreError } = await coreQuery.order('start_at', { ascending: true });

      if (coreError) {
        logger.error('[EventsService] Error fetching events (core fields)', coreError, { 
          scope, 
          startDate, 
          endDate,
          errorCode: coreError.code,
          errorMessage: coreError.message
        });
        throw new Error(getSupabaseErrorMessage(coreError));
      }

      return (coreData || []) as Event[];
    }

    if (error) {
      logger.error('[EventsService] Error fetching events', error, { 
        scope, 
        startDate, 
        endDate,
        errorCode: error.code,
        errorMessage: error.message,
        errorDetails: error.details,
        errorHint: error.hint
      });
      throw new Error(getSupabaseErrorMessage(error));
    }

    return (data || []) as Event[];
  }

  /**
   * Get single event by ID
   */
  static async getEvent(eventId: string, workspaceId: string): Promise<Event | null> {
    const { data, error } = await supabase
      .from('events')
      .select('*')
      .eq('id', eventId)
      .eq('workspace_id', workspaceId)
      .maybeSingle();

    if (error) {
      logger.error('[EventsService] Error fetching event', error, { eventId, workspaceId });
      throw new Error(getSupabaseErrorMessage(error));
    }

    return data as Event | null;
  }

  /**
   * Create an event
   */
  static async createEvent(input: CreateEventInput): Promise<Event> {
    logger.info('[EventsService.createEvent] Starting', { 
      workspaceId: input.workspace_id, 
      title: input.title,
      type: input.type,
      startAt: input.start_at,
      leadId: input.lead_id
    });
    
    // Build payload with only fields that exist in schema
    const eventPayload: any = {
      workspace_id: input.workspace_id,
      title: input.title,
      type: input.type,
      start_at: input.start_at,
      ...(input.end_at && { end_at: input.end_at }),
      ...(input.lead_id && { lead_id: input.lead_id }),
      ...(input.notes && { notes: input.notes }),
    };
    
    // Add optional fields only if they exist in schema (check via test query first)
    try {
      // Test if priority column exists by trying to select it
      const testQuery = await supabase
        .from('events')
        .select('priority, status, reminder_offsets_minutes')
        .limit(1);
      
      // If no error, columns exist - add them
      if (!testQuery.error || testQuery.error.code !== 'PGRST204') {
        eventPayload.status = 'scheduled';
        eventPayload.priority = input.priority || 'normal';
        eventPayload.reminder_offsets_minutes = input.reminder_offsets_minutes || [60, 15, 0];
      } else {
        // Columns don't exist yet - create without them (will fail gracefully)
        logger.warn('[EventsService.createEvent] priority/status columns not found, creating event without them', {
          workspaceId: input.workspace_id
        });
      }
    } catch (error: any) {
      // If test query fails, assume columns don't exist
      logger.warn('[EventsService.createEvent] Could not verify schema, creating event without optional fields', {
        error: error.message,
        workspaceId: input.workspace_id
      });
    }

    let { data, error } = await supabase
      .from('events')
      .insert([eventPayload])
      .select()
      .single();

    // If error is about missing columns (schema cache), retry without optional fields
    if (error && (error.code === 'PGRST204' || error.message?.includes('schema cache') || error.message?.includes('priority') || error.message?.includes('status'))) {
      logger.warn('[EventsService.createEvent] Schema cache issue, retrying without optional fields', {
        errorCode: error.code,
        errorMessage: error.message,
        workspaceId: input.workspace_id
      });
      
      // Retry with only core fields (no priority, status, reminder_offsets_minutes)
      const corePayload: any = {
        workspace_id: input.workspace_id,
        title: input.title,
        type: input.type,
        start_at: input.start_at,
        ...(input.end_at && { end_at: input.end_at }),
        ...(input.lead_id && { lead_id: input.lead_id }),
        ...(input.notes && { notes: input.notes }),
      };
      
      const retryResult = await supabase
        .from('events')
        .insert([corePayload])
        .select()
        .single();
      
      if (retryResult.error) {
        logger.error('[EventsService] Error creating event (core fields)', retryResult.error, { 
          workspaceId: input.workspace_id,
          errorCode: retryResult.error.code,
          errorMessage: retryResult.error.message,
          errorDetails: retryResult.error.details,
          errorHint: retryResult.error.hint,
          corePayload
        });
        throw new Error(getSupabaseErrorMessage(retryResult.error));
      }
      
      data = retryResult.data;
      error = null;
      logger.info('[EventsService.createEvent] Success (core fields only)', { 
        eventId: data?.id, 
        workspaceId: input.workspace_id 
      });
    }

    if (error) {
      logger.error('[EventsService] Error creating event', error, { 
        workspaceId: input.workspace_id,
        errorCode: error.code,
        errorMessage: error.message,
        errorDetails: error.details,
        errorHint: error.hint,
        eventPayload
      });
      throw new Error(getSupabaseErrorMessage(error));
    }

    logger.info('[EventsService.createEvent] Success', { 
      eventId: data?.id, 
      workspaceId: input.workspace_id 
    });
    
    return data as Event;
  }

  /**
   * Update an event
   */
  static async updateEvent(input: UpdateEventInput): Promise<Event> {
    const { id, workspace_id, ...updates } = input;

    const { data, error } = await supabase
      .from('events')
      .update(updates as any)
      .eq('id', id)
      .eq('workspace_id', workspace_id)
      .select()
      .single();

    if (error) {
      logger.error('[EventsService] Error updating event', error, { eventId: input.id, workspaceId: input.workspace_id });
      throw new Error(getSupabaseErrorMessage(error));
    }

    return data as Event;
  }

  /**
   * Delete an event
   */
  static async deleteEvent(eventId: string, workspaceId: string): Promise<void> {
    const { error } = await supabase
      .from('events')
      .delete()
      .eq('id', eventId)
      .eq('workspace_id', workspaceId);

    if (error) {
      logger.error('[EventsService] Error deleting event', error, { eventId, workspaceId });
      throw new Error(getSupabaseErrorMessage(error));
    }
  }
}

